/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { track } from '@vercel/analytics';
import * as Sentry from '@sentry/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/components/AuthProvider';
import { motion, AnimatePresence } from 'motion/react';
import { 
  User, 
  Briefcase, 
  Calendar, 
  DollarSign, 
  Plus, 
  PlusCircle,
  Check, 
  Upload, 
  TrendingUp, 
  Percent, 
  ChevronRight, 
  ChevronLeft,
  X,
  FileText,
  FilePlus,
  ArrowRight,
  SkipForward,
  CreditCard,
  Lock,
  Eye,
  EyeOff,
  Zap,
  Crown,
  Clock,
  ShieldCheck,
  Target,
  FileStack
} from 'lucide-react';
import { differenceInDays, parseISO, format, addDays } from 'date-fns';
import { getCurrencySymbol } from '@/lib/utils';

// --- Types ---

interface Booking {
  id: string;
  company: string;
  role?: string;
  startDate: string; // ISO yyyy-MM-dd
  endDate: string;   // ISO yyyy-MM-dd
  rate: number;
  rateType: 'daily' | 'hourly';
  hoursPerDay?: number;
  days: number;
  earnings: number;
  status: 'Active' | 'Pending' | 'Paid';
  currency: string;
  importSource?: 'csv' | 'manual';
  reasoning?: string;
}

interface YearlyPerformance {
  year: string;
  totalEarned: number;
  totalDaysWorked: number;
  avgDayRate: number;
  reasoning?: string;
}

type OnboardingStep = 
  | 'SIGNUP' 
  | 'PROFILE' 
  | 'CSV_IMPORT'
  | 'FIRST_BOOKING' 
  | 'ADD_MORE_LOOP' 
  | 'PAST_EARNINGS' 
  | 'GOALS' 
  | 'SECURE_TRIAL' 
  | 'COMPLETE';

// --- Helpers ---

const formatCurrency = (amount: number, currency = 'USD'): string => {
  const safeAmount = isNaN(amount) || amount == null ? 0 : amount;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(safeAmount);
};

const formatDateRange = (start: string, end: string) => {
  if (!start || !end) return '';
  try {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    return `${format(startDate, 'MMM dd')} – ${format(endDate, 'MMM dd, yyyy')}`;
  } catch (e) {
    return '';
  }
};

// --- Components ---

export default function OnboardingPage() {
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  const srcParam = searchParams.get('src');
  const [selectedPlan, setSelectedPlan] = useState<'junior' | 'lead'>(
    planParam === 'junior' ? 'junior' : 'lead'
  );
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const { user, markOnboardingComplete, onboardingComplete } = useAuth();
  const navigate = useNavigate();

  // If this user already completed onboarding (e.g. refreshed on the COMPLETE screen),
  // skip straight to the dashboard instead of showing an empty form.
  useEffect(() => {
    if (onboardingComplete === true) navigate('/', { replace: true });
  }, [onboardingComplete, navigate]);

  // Set to true when Stripe Checkout is wired up (Phase 5B).
  // While false, the SECURE_TRIAL step is completely skipped — users go GOALS → COMPLETE.
  const ENABLE_STRIPE_ONBOARDING = false;

  const [step, setStep] = useState<OnboardingStep>('SIGNUP');
  const [returnStep, setReturnStep] = useState<OnboardingStep | null>(null);

  useEffect(() => {
    track('onboarding_step', { step });
  }, [step]);
  
  // Auth State
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [profileError, setProfileError] = useState('');

  // If user is already authenticated, skip SIGNUP and pre-populate email.
  // Use functional update so we only advance from SIGNUP → PROFILE once.
  // Any subsequent auth events (token refresh, re-auth) must NOT reset a
  // later step back to PROFILE — that was causing the "stuck on PROFILE" bug.
  useEffect(() => {
    if (user) {
      setEmail(user.email ?? '');
      setStep(prev => prev === 'SIGNUP' ? 'PROFILE' : prev);
    }
  }, [user]);

  // Profile State
  const [role, setRole] = useState('');
  const [defaultRateType, setDefaultRateType] = useState<'daily' | 'hourly'>('daily');
  const [defaultRate, setDefaultRate] = useState('');

  // CSV Import State
  const [csvContent, setCsvContent] = useState({
    currentYear: false,
    previousYear: false,
    multipleYears: false,
    pastSummary: false
  });
  const [importSuccess, setImportSuccess] = useState<{count: number, type: 'bookings' | 'years'} | null>(null);
  const [pendingImportedBookings, setPendingImportedBookings] = useState<Booking[]>([]);
  const [totalImportedBookings, setTotalImportedBookings] = useState(0);
  const [isImportedHistory, setIsImportedHistory] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [csvImportMode, setCsvImportMode] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [showCheckboxError, setShowCheckboxError] = useState(false);

  // Bookings State
  const [bookedJobs, setBookedJobs] = useState<Booking[]>([]);
  const [currentJob, setCurrentJob] = useState({
    company: '',
    startDate: '',
    endDate: '',
    rate: '',
    rateType: 'daily' as 'daily' | 'hourly',
    hoursPerDay: '8',
    currency: 'USD'
  });
  const [manualBilled, setManualBilled] = useState<string | null>(null);
  const [importedDays, setImportedDays] = useState<number | null>(null);
  const [importedEarnings, setImportedEarnings] = useState<number | null>(null);
  const [hasManuallyEditedBilled, setHasManuallyEditedBilled] = useState(false);
  const [bookingYear, setBookingYear] = useState(new Date().getFullYear().toString());
  const [isEarningsFocused, setIsEarningsFocused] = useState(false);

  // Historical State
  const [performanceHistory, setPerformanceHistory] = useState<YearlyPerformance[]>([]);

  // Goals State
  const [annualGoal, setAnnualGoal] = useState('');
  const [isGoalFocused, setIsGoalFocused] = useState(false);
  const [showManualEntryChoice, setShowManualEntryChoice] = useState(false);
  const [targetYearForChoice, setTargetYearForChoice] = useState<string | null>(null);

  // Parsing State
  const [isParsing, setIsParsing] = useState(false);
  const [videoIndex, setVideoIndex] = useState(0);
  const loadingVideos = [
    "https://res.cloudinary.com/dlmbskxuc/video/upload/v1776708138/loading_firsthold2_srwg3e.mp4",
    "https://res.cloudinary.com/dlmbskxuc/video/upload/v1776710228/firsthold_car_loader2_wuwmvf.mp4"
  ];

  useEffect(() => {
    // Rotation logic - only cycle if user has seen it before or we want variation
    // For now, let's keep it simple and respect the default requested
  }, [isParsing]);

  const [isTakingLonger, setIsTakingLonger] = useState(false);
  const [isSweating, setIsSweating] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Calculations ---

  const currentJobStats = useMemo(() => {
    if (!currentJob.startDate || !currentJob.endDate) {
      return { days: 0, billed: 0 };
    }
    const start = parseISO(currentJob.startDate);
    const end = parseISO(currentJob.endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { days: 0, billed: 0 };
    }

    const days = Math.max(0, differenceInDays(end, start) + 1);
    const rate = parseFloat(currentJob.rate || '0');
    
    let billed = 0;
    if (currentJob.rateType === 'daily') {
      billed = days * rate;
    } else {
      billed = days * parseFloat(currentJob.hoursPerDay) * rate;
    }
    
    return { days, billed: manualBilled !== null ? parseFloat(manualBilled) : billed };
  }, [currentJob, manualBilled]);

  const totalProjectedYTD = useMemo(() => {
    return bookedJobs.reduce((acc, job) => {
      const safeEarnings = isNaN(job.earnings) || job.earnings == null ? 0 : job.earnings;
      return acc + safeEarnings;
    }, 0);
  }, [bookedJobs]);

  useEffect(() => {
    if (step === 'PAST_EARNINGS' && bookedJobs.length > 0) {
      const groupedByYear: Record<string, { earned: number, days: number }> = {};
      
      bookedJobs.forEach(job => {
        // Bookings must have at least client and earnings to count towards totals
        if (!job.company || job.earnings <= 0) return;
        
        const year = job.startDate?.split('-')[0];
        if (!year) return;
        
        if (!groupedByYear[year]) {
          groupedByYear[year] = { earned: 0, days: 0 };
        }
        groupedByYear[year].earned += job.earnings || 0;
        groupedByYear[year].days += job.days || 0;
      });

      setPerformanceHistory(prev => {
        const newHistory = [...prev];
        Object.entries(groupedByYear).forEach(([year, data]) => {
          const existingIdx = newHistory.findIndex(h => h.year === year);
          if (existingIdx !== -1) {
            // Check if we actually need to update to avoid infinite loops
            const existing = newHistory[existingIdx];
            if (existing.totalEarned !== data.earned || existing.totalDaysWorked !== data.days) {
              newHistory[existingIdx] = {
                year,
                totalEarned: data.earned,
                totalDaysWorked: data.days,
                avgDayRate: data.days > 0 ? data.earned / data.days : 0
              };
            }
          } else {
            newHistory.push({
              year,
              totalEarned: data.earned,
              totalDaysWorked: data.days,
              avgDayRate: data.days > 0 ? data.earned / data.days : 0
            });
          }
        });
        
        // Only trigger state update if something actually changed
        const hasChanged = JSON.stringify(newHistory) !== JSON.stringify(prev);
        return hasChanged ? newHistory.sort((a, b) => parseInt(b.year) - parseInt(a.year)) : prev;
      });
    }
  }, [step, bookedJobs]);

  const trialEndDate = useMemo(() => format(addDays(new Date(), 14), 'MMM dd, yyyy'), []);

  const calculateDaysFromDates = (startStr: string, endStr: string) => {
    if (!startStr || !endStr) return 0;
    try {
      const start = parseISO(startStr);
      const end = parseISO(endStr);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
      return Math.max(0, differenceInDays(end, start) + 1);
    } catch {
      return 0;
    }
  };

  const syncBookingCalculations = (
    field: 'rate' | 'days' | 'earnings' | 'dates', 
    overrides: { rate?: string, days?: number, earnings?: number, start?: string, end?: string } = {}
  ) => {
    const rateS = overrides.rate !== undefined ? overrides.rate : currentJob.rate;
    const daysV = overrides.days !== undefined ? overrides.days : (importedDays ?? 0);
    const earningsV = overrides.earnings !== undefined ? overrides.earnings : (importedEarnings ?? 0);
    const startS = overrides.start !== undefined ? overrides.start : currentJob.startDate;
    const endS = overrides.end !== undefined ? overrides.end : currentJob.endDate;

    const rateNum = parseFloat(rateS || '0');
    const hpdNum = parseFloat(currentJob.hoursPerDay || '8');
    const isDaily = currentJob.rateType === 'daily';

    if (field === 'dates') {
      const newDays = calculateDaysFromDates(startS, endS);
      setImportedDays(newDays);
      if (!hasManuallyEditedBilled && rateNum > 0) {
        setImportedEarnings(isDaily ? newDays * rateNum : newDays * hpdNum * rateNum);
      }
    } else if (field === 'rate') {
      if (!hasManuallyEditedBilled && (overrides.days !== undefined || (importedDays !== null && importedDays !== 0))) {
        const effectiveDays = overrides.days !== undefined ? overrides.days : (importedDays || 0);
        setImportedEarnings(isDaily ? effectiveDays * rateNum : effectiveDays * hpdNum * rateNum);
      }
    } else if (field === 'days') {
      if (!hasManuallyEditedBilled && rateNum > 0) {
        setImportedEarnings(isDaily ? daysV * rateNum : daysV * hpdNum * rateNum);
      }
    } else if (field === 'earnings') {
      // Only back-calculate days if they are currently 0/missing and we have a valid rate/earning
      if (rateNum > 0 && (daysV === 0)) {
        const calcDays = isDaily ? earningsV / rateNum : earningsV / (hpdNum * rateNum);
        if (calcDays >= 0.5) {
          setImportedDays(calcDays);
        }
      }
    }
  };

  // --- Handlers ---

  const handleAddBooking = () => {
    const newBooking: Booking = {
      id: Date.now().toString(),
      company: currentJob.company,
      startDate: currentJob.startDate,
      endDate: currentJob.endDate,
      rate: parseFloat(currentJob.rate) || 0,
      rateType: currentJob.rateType,
      hoursPerDay: currentJob.rateType === 'hourly' ? (parseFloat(currentJob.hoursPerDay) || 8) : undefined,
      days: importedDays ?? currentJobStats.days,
      earnings: importedEarnings ?? currentJobStats.billed,
      status: 'Active',
      currency: currentJob.currency,
      // 'csv' when confirming a Gemini-imported booking; 'manual' when typed by hand
      importSource: pendingImportedBookings.length > 0 ? 'csv' : 'manual',
    };
    
    setBookedJobs(prev => [...prev, newBooking]);
    
    if (pendingImportedBookings.length > 0) {
      setPendingImportedBookings(prevPending => {
        const remaining = prevPending.slice(1);
        
        if (remaining.length > 0) {
          const next = remaining[0];
          setCurrentJob({
            company: next.company,
            startDate: next.startDate,
            endDate: next.endDate,
            rate: next.rate === 0 ? '' : next.rate.toString(),
            rateType: next.rateType,
            hoursPerDay: '8',
            currency: next.currency
          });
          setImportedDays(next.days);
          setImportedEarnings(next.earnings);
          setHasManuallyEditedBilled(false);
          setBookingYear(new Date().getFullYear().toString());
        } else {
          // No more bookings, advance to next step
          setCurrentJob({
            company: '',
            startDate: '',
            endDate: '',
            rate: '',
            rateType: 'daily',
            hoursPerDay: '8',
            currency: 'USD'
          });
          setManualBilled(null);
          setImportedDays(null);
          setImportedEarnings(null);
          setHasManuallyEditedBilled(false);
          setBookingYear(new Date().getFullYear().toString());
          
          if (returnStep) {
            setStep(returnStep);
            setReturnStep(null);
          } else {
            setStep('ADD_MORE_LOOP');
          }

          setCsvImportMode(false);
          setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
          setImportSuccess(null);
          setImportError(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
        
        return remaining;
      });
      return;
    }

    setCurrentJob({
      company: '',
      startDate: '',
      endDate: '',
      rate: '',
      rateType: 'daily',
      hoursPerDay: '8',
      currency: 'USD'
    });
    setManualBilled(null);
    setImportedDays(null);
    setImportedEarnings(null);
    setHasManuallyEditedBilled(false);
    setBookingYear(new Date().getFullYear().toString());
    
    if (returnStep && returnStep !== 'PAST_EARNINGS') {
      setStep(returnStep);
      setReturnStep(null);
    } else {
      setStep('ADD_MORE_LOOP');
    }
  };

  const handleBulkConfirm = () => {
    console.log(`Bulk confirming ${pendingImportedBookings.length} bookings.`);
    const bulkBookings = pendingImportedBookings.map(b => ({
      ...b,
      earnings: isNaN(b.earnings) || b.earnings == null ? 0 : b.earnings
    }));
    setBookedJobs([...bookedJobs, ...bulkBookings]);
    setPendingImportedBookings([]);
    setShowBulkConfirm(false);
    
    setCsvImportMode(false);
    setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
    setImportSuccess(null);
    setImportError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (returnStep) {
      setStep(returnStep);
      setReturnStep(null);
    } else {
      setStep('PAST_EARNINGS');
    }
  };

  const handleAddDetailedBooking = (year: string) => {
    setReturnStep('PAST_EARNINGS');
    setBookingYear(year);
    // Reset currentJob for the new entry
    setCurrentJob({
      company: '',
      startDate: `${year}-01-01`,
      endDate: `${year}-01-01`,
      rate: '',
      rateType: 'daily',
      hoursPerDay: '8',
      currency: currentJob.currency
    });
    setImportedDays(null);
    setImportedEarnings(null);
    setHasManuallyEditedBilled(false);
    setStep('FIRST_BOOKING');
    setShowManualEntryChoice(false);
  };

  const handleAddYear = () => {
    // First row defaults to current year; each subsequent row decrements from the last.
    const newYear = performanceHistory.length > 0
      ? (parseInt(performanceHistory[performanceHistory.length - 1].year) - 1).toString()
      : new Date().getFullYear().toString();
    setPerformanceHistory([...performanceHistory, { year: newYear, totalEarned: 0, totalDaysWorked: 0, avgDayRate: 0 }]);
  };

  const updateYearData = (index: number, field: keyof YearlyPerformance, value: string | number) => {
    const updated = [...performanceHistory];

    let processedValue = value;
    if (field === 'totalEarned' && typeof value === 'string') {
      processedValue = parseFloat(value.replace(/[^0-9]/g, '')) || 0;
    }

    updated[index] = { ...updated[index], [field]: processedValue };
    if (field === 'totalEarned' || field === 'totalDaysWorked') {
      const earned = field === 'totalEarned' ? Number(processedValue) : updated[index].totalEarned;
      const days = field === 'totalDaysWorked' ? Number(processedValue) : updated[index].totalDaysWorked;
      updated[index].avgDayRate = days > 0 ? earned / days : 0;
    }

    // After editing the year field, re-sort descending so the list stays in order.
    // (Sorting already happens on CSV import; this covers manual edits.)
    if (field === 'year') {
      updated.sort((a, b) => parseInt(b.year) - parseInt(a.year));
    }

    setPerformanceHistory(updated);
  };

  // Thin client wrapper — actual Gemini logic lives in /api/parse-csv (server-side).
  // API key never touches the client bundle.
  const parseFileWithGemini = async (
    checkedLabels: string,
    fileData: string,
    isHistoricalView: boolean,
    userDefaultRateType: 'daily' | 'hourly',
    userDefaultCurrency: string = 'USD',
    mimeType: string = 'text/plain',
    fileName = '',
    fileType = '',
  ) => {
    console.log('[Gemini] sending to /api/parse-csv | mimeType:', mimeType, '| isHistoricalView:', isHistoricalView, '| checkedLabels:', checkedLabels);

    // Forward the user's JWT so the server can write to gemini_parse_log under RLS.
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const res = await fetch('/api/parse-csv', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        fileData,
        mimeType,
        checkedLabels,
        isHistorical: isHistoricalView,
        defaultRateType: userDefaultRateType,
        defaultCurrency: userDefaultCurrency,
        fileName,
        fileType,
      }),
    });

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.error || 'Parsing failed on server.');
    }
    return json;
  };


  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>, isHistorical: boolean = false) => {
    const file = e.target.files?.[0];
    console.log('[Gemini] handleCsvUpload fired, file:', file?.name, file?.type, file?.size);
    if (!file) return;

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isExcel = !isPdf && (file.type.includes('spreadsheet') || file.type.includes('excel') ||
      file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls'));

    setIsParsing(true);
    setIsTakingLonger(false);
    setIsSweating(false);
    setElapsedSeconds(0);
    setImportError(null);
    setImportSuccess(null);
    setWarnings([]);
    setCsvImportMode(true);

    const effectiveIsHistorical = isHistorical || returnStep === 'PAST_EARNINGS';

    // Timer for display
    const counterTimer = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    const takingLongerTimer = setTimeout(() => setIsTakingLonger(true), 30000);
    const sweatingTimer = setTimeout(() => setIsSweating(true), 50000);

    const checkedLabels = isHistorical ? "Historical Yearly Totals" : [
          csvContent.currentYear && "This Year's Bookings",
          csvContent.previousYear && "Last Year's Bookings",
          csvContent.multipleYears && "Multiple Years",
          csvContent.pastSummary && "Annual Sums Only"
        ].filter(Boolean).join(", ");

    try {
      let fileData = "";
      let mimeType = "text/plain";

      if (isPdf) {
        mimeType = "application/pdf";
        fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
            const base64 = result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      } else if (isExcel) {
        mimeType = file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      } else {
        const fileText = await file.text();

        const preprocessCsv = (text: string): string => {
          return text
            .split(/\r?\n/)
            .filter(row => {
              // Keep rows that have at least some content that isn't just commas or whitespace
              const cleaned = row.replace(/,/g, '').trim();
              return cleaned.length > 0;
            })
            .join('\n');
        };

        fileData = preprocessCsv(fileText);
      }

      const detectedCurrency = !isPdf && !isExcel && (fileData.includes('£') || /\bGBP\b/i.test(fileData) || /\bpounds?\b/i.test(fileData)) ? 'GBP'
        : !isPdf && !isExcel && (fileData.includes('€') || /\bEUR\b/i.test(fileData)) ? 'EUR'
        : 'USD';

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("TIMEOUT")), 120000)
      );

      const result = await Promise.race([
        parseFileWithGemini(checkedLabels, fileData, effectiveIsHistorical, defaultRateType, currentJob.currency || 'USD', mimeType, file.name, file.type),
        timeoutPromise
      ]) as any[];
      
      clearInterval(counterTimer);
      clearTimeout(takingLongerTimer);
      clearTimeout(sweatingTimer);
      setIsTakingLonger(false);
      setIsSweating(false);
      
      if (!Array.isArray(result) || result.length === 0) {
        setImportError("No records found. Your file may have an unusual format — try a simpler export.");
        setIsParsing(false);
        return;
      }

      const currentYearNum = new Date().getFullYear();
      const prevYearNum = currentYearNum - 1;
      console.log("Internal Year Check:", { current: currentYearNum, prev: prevYearNum, mode: csvContent });

      // Split records by type
      const historyRecords = result.filter((item: any) => 
        item.type === 'summary' || (!item.type && item.year && (item.totalEarned || item.totalDaysWorked))
      );
      
      const bookingRecords = result.filter((item: any) => {
        const hasClient = item.clientName && typeof item.clientName === 'string' && item.clientName.trim().length > 0;
        return (item.type === 'booking' || (!item.type && (item.rate || item.totalBilled))) && hasClient;
      });

      // Process History
      let history: YearlyPerformance[] = historyRecords.map((s: any) => {
        const clean = (val: any) => {
          if (typeof val === 'number') return val;
          return parseFloat(String(val || '').replace(/[^0-9.]/g, '')) || 0;
        };
        const earned = clean(s.totalEarned || s.totalBilled);
        const worked = clean(s.totalDaysWorked || s.totalDays);
        
        // Improve year inference: use context from UI selection if AI is vague
        let yearVal = s.year?.toString();
        if (!yearVal || yearVal.length < 4) {
          if (csvContent.previousYear) yearVal = prevYearNum.toString();
          else if (csvContent.currentYear) yearVal = currentYearNum.toString();
          else yearVal = currentYearNum.toString();
        }

        return {
          year: yearVal,
          totalEarned: earned,
          totalDaysWorked: worked,
          avgDayRate: worked > 0 ? earned / worked : 0,
          reasoning: s.reasoning
        };
      });

      // If user selected a specific year mode, ignore any other years the AI might have halluncinated (e.g. from header timestamps)
      if (csvContent.currentYear || csvContent.previousYear) {
        const targetYear = csvContent.currentYear ? currentYearNum.toString() : prevYearNum.toString();
        history = history.filter(h => h.year === targetYear || (Math.abs(h.totalEarned) > 0));
        // If everything was filtered out or we have duplicates, force the strongest match to the target year
        if (history.length > 0) {
          history = history.map(h => ({ ...h, year: targetYear }));
        }
      }

      if ((isHistorical === true || csvContent.pastSummary) && history.length > 0) {
        console.log("Extracted History:", history);
        setPerformanceHistory(prev => {
          // Deduplicate incoming history records that might be identical (AI confusion)
          // Also deduplicate if same exact values occur twice (v. likely the same data segment)
          const uniqueHistory = history.filter((h, i, self) => 
            i === self.findIndex(t => t.year === h.year || (Math.abs(t.totalEarned - h.totalEarned) < 1 && t.totalDaysWorked === h.totalDaysWorked && h.totalEarned > 0))
          );

          const newHistory = [...prev];
          uniqueHistory.forEach(newYear => {
            const existingIdx = newHistory.findIndex(h => h.year === newYear.year);
            if (existingIdx !== -1) {
              newHistory[existingIdx] = newYear;
            } else {
              newHistory.push(newYear);
            }
          });
          return newHistory.sort((a, b) => parseInt(b.year) - parseInt(a.year));
        });
        setIsImportedHistory(true);
      }

      // Process Bookings
      // For "Other Years" imports, infer the dominant year from what the AI parsed
      // rather than falling back to currentYearNum (which would wrongly stamp 2024 data as 2026)
      let multipleYearsDefaultYear = currentYearNum;
      if (csvContent.multipleYears) {
        const yearCounts: Record<number, number> = {};
        for (const b of bookingRecords) {
          for (const dateField of [b.startDate, b.endDate]) {
            const match = String(dateField || '').match(/\b(20\d{2})\b/);
            if (match) {
              const y = parseInt(match[1]);
              yearCounts[y] = (yearCounts[y] || 0) + 1;
            }
          }
        }
        const entries = Object.entries(yearCounts);
        if (entries.length > 0) {
          multipleYearsDefaultYear = parseInt(entries.reduce((a, b) => (b[1] > a[1] ? b : a))[0]);
        }
      }

      const rawBookings: Booking[] = bookingRecords.map((b: any) => {
        console.log("Raw AI Booking:", b);
        const cleanNum = (val: any) => {
          if (typeof val === 'number') return val;
          // Remove everything except digits and decimal point to handle commas correctly (e.g. 14,000 -> 14000)
          return parseFloat(String(val || '').replace(/[^0-9.]/g, '')) || 0;
        };

        const detectCurrency = (val: any, existing: string) => {
          const s = String(val || '').toUpperCase();
          if (s.includes('£') || s.includes('GBP') || s.includes('POUND')) return 'GBP';
          if (s.includes('€') || s.includes('EUR')) return 'EUR';
          if (s.includes('$') || s.includes('USD')) return 'USD';
          return existing || 'USD';
        };

        let rate = cleanNum(b.rate);
        let earnings = cleanNum(b.totalBilled) || cleanNum(b.totalEarned);
        let days = parseFloat(String(b.totalDays || b.totalDaysWorked || '').replace(/[^0-9.]/g, '')) || 0;

        const defaultYear = csvContent.previousYear ? prevYearNum : (csvContent.multipleYears ? multipleYearsDefaultYear : currentYearNum);
        const sanitizeDate = (dateStr: string) => {
          if (!dateStr || typeof dateStr !== 'string' || dateStr.trim() === '') return `${defaultYear}-01-01`;
          
          // ISO format check (YYYY-MM-DD or YYYY-M-D)
          const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
          if (isoMatch) return dateStr;
          
          const parts = dateStr.split(/[\/\-\.]/).map(p => p.trim());
          if (parts.length >= 3) {
            // Find 4 digit year
            let yearIdx = parts.findIndex(p => p.length === 4);
            let year = yearIdx !== -1 ? parts[yearIdx] : '';
            
            if (!year) {
              // Try to find which part is the year (guessing last part)
              if (parts[2].length === 2) {
                year = '20' + parts[2];
                yearIdx = 2;
              } else {
                year = defaultYear.toString();
              }
            }

            // Assign month and day from remaining parts
            const others = parts.filter((_, i) => i !== yearIdx);
            // Default to US format MM/DD if year was at end, or DD/MM if year was at start
            // If year was first, assume year-month-day
            let month = '01';
            let day = '01';
            
            if (yearIdx === 0) {
              month = others[0] || '01';
              day = others[1] || '01';
            } else {
              month = others[0] || '01';
              day = others[1] || '01';
            }
            
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
          
          if (parts.length === 2) {
            return `${defaultYear}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          }
          
          return `${defaultYear}-01-01`;
        };

        const sDate = sanitizeDate(b.startDate);
        const eDate = b.endDate ? sanitizeDate(b.endDate) : (b.startDate ? sDate : sanitizeDate(''));
        
        // Smart Inference: Cross-reference fields if one is missing
        if (days === 0 && sDate && eDate) {
          const start = parseISO(sDate);
          const end = parseISO(eDate);
          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            days = Math.max(0, differenceInDays(end, start) + 1);
          }
        }

        if (rate === 0 && earnings > 0 && days > 0) {
          rate = earnings / days;
        } else if (earnings === 0 && rate > 0 && days > 0) {
          earnings = rate * days;
        }

        // Detect currency from various fields
        let currency = detectCurrency(b.currency, '');
        if (currency === 'USD') currency = detectCurrency(b.totalBilled || b.totalEarned, 'USD');
        if (currency === 'USD') currency = detectCurrency(b.rate, 'USD');

        return {
          id: (Date.now() + Math.random()).toString(),
          company: b.clientName || '',
          startDate: sDate,
          endDate: eDate,
          rate,
          rateType: (b.rateType || (String(b.rate).toLowerCase().includes('hour') ? 'hourly' : 'daily')) as 'daily' | 'hourly',
          days: days || 0,
          earnings: earnings || 0,
          status: 'Active' as const,
          currency: currency !== 'USD' ? currency : detectedCurrency,
          importSource: 'csv' as const,
          reasoning: b.reasoning
        };
      });

      // Deduplicate bookings
      const bookings = rawBookings.filter((b, index, self) => 
        index === self.findIndex(t => (
          t.company === b.company && 
          t.startDate === b.startDate && 
          t.endDate === b.endDate &&
          Math.abs(t.earnings - b.earnings) < 1
        ))
      );

      if (bookings.length > 0) {
        setPendingImportedBookings(bookings);
        setTotalImportedBookings(bookings.length);
        const first = bookings[0];
        setImportedDays(first.days);
        setImportedEarnings(first.earnings);
        setHasManuallyEditedBilled(false);
        setCurrentJob({
          company: first.company,
          startDate: first.startDate,
          endDate: first.endDate,
          rate: first.rate === 0 ? '' : first.rate.toString(),
          rateType: first.rateType,
          hoursPerDay: '8',
          currency: first.currency || 'USD'
        });
        
        setImportSuccess({ count: bookings.length, type: 'bookings' });
        setTimeout(() => {
          setStep('FIRST_BOOKING');
          setIsParsing(false);
        }, 1500);
      } else if (history.length > 0 && (isHistorical === true || csvContent.pastSummary || csvContent.multipleYears)) {
        // Only fallback to yearly summaries if we are actually in a summary-compatible mode
        setImportSuccess({ count: history.length, type: 'years' });
        setTimeout(() => {
          if (returnStep) {
            setStep(returnStep);
            setReturnStep(null);
          } else {
            setStep('PAST_EARNINGS');
          }
          setIsParsing(false);
        }, 1500);
      } else {
        setImportError("No individual booking records found. Verify you selected the correct file content type (e.g. Other Years vs This Year's Bookings).");
        setIsParsing(false);
      }
      setIsParsing(false);
    } catch (error: any) {
      clearInterval(counterTimer);
      clearTimeout(takingLongerTimer);
      clearTimeout(sweatingTimer);
      setIsTakingLonger(false);
      setIsSweating(false);
      console.error("Parsing error:", error);
      Sentry.captureException(error, { tags: { flow: 'file-parse', route: 'onboarding' } });
      setIsParsing(false);
      if (error.message === "TIMEOUT") {
        setImportError("Request timed out (120s). The AI is currently very slow or the file is complex. Please try again.");
      } else {
        setImportError(error.message || "Could not parse file. Please try again or enter data manually.");
      }
    }
  };

  // --- Render Helpers ---

  const inputClasses = "w-full bg-white/50 border border-gray-200 rounded-2xl py-3 pl-12 pr-4 text-base font-bold text-[#1A1F36] focus:outline-none focus:border-[#4A6CF7]/40 focus:bg-white/60 transition-all";
  const dateInputClasses = "w-full bg-white/50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-bold text-[#1A1F36] focus:outline-none focus:border-[#4A6CF7]/20 focus:bg-white/60 transition-all appearance-none";
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const labelClasses = "block uppercase text-[10px] font-bold tracking-[0.2em] text-[#45556c] mb-1.5 ml-1";
  const btnRowClasses = "flex items-center gap-3 w-full mt-auto pt-3";
  const backBtnClasses = "px-5 py-3.5 rounded-xl font-black uppercase text-[10px] tracking-[0.2em] text-[#45556c] hover:bg-black/5 transition-all flex items-center gap-2";
  const primaryBtnClasses = "flex-1 bg-black text-white py-3.5 rounded-full font-black uppercase text-[10px] tracking-[0.2em] hover:scale-[1.05] active:scale-[0.95] transition-all shadow-xl disabled:opacity-20";

  const renderSignup = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col h-full"
    >
      <div className="space-y-4">
        <div className="space-y-1 flex flex-col items-start text-left w-full max-w-md mx-auto">
          <div className="flex items-center gap-2 px-3 py-1 bg-black text-white rounded-full w-fit">
            <Zap className="w-3 h-3" />
            <span className="text-[10px] font-black uppercase tracking-widest">14-Day Free Trial</span>
          </div>
          <h1 className="text-5xl font-black tracking-tight text-[#1A1F36] leading-[0.9]">
            Let's get you <br />
            <span className="text-[#4A6CF7]">set up!</span>
          </h1>
        </div>

        <div className="w-full max-w-md space-y-3 mx-auto">
          <div className="grid grid-cols-2 gap-4">
            <div className="group relative">
              <label className={labelClasses}>First Name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
                <input type="text" placeholder="" value={firstName} onChange={e => setFirstName(e.target.value)} className={inputClasses} />
              </div>
            </div>
            <div className="group relative">
              <label className={labelClasses}>Last Name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
                <input type="text" placeholder="" value={lastName} onChange={e => setLastName(e.target.value)} className={inputClasses} />
              </div>
            </div>
          </div>
          <div className="group relative">
            <label className={labelClasses}>Email Address</label>
            <div className="relative">
              <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
              <input type="email" placeholder="name@example.com" value={email} onChange={e => setEmail(e.target.value)} className={inputClasses} />
            </div>
          </div>
          <div className="group relative">
            <label className={labelClasses}>Create Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
              <input type={showPassword ? "text" : "password"} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className={inputClasses} />
              <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-black/20 hover:text-[#4A6CF7] transition-colors focus:outline-none">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="group relative">
            <label className={labelClasses}>Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
              <input type={showConfirmPassword ? "text" : "password"} placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={inputClasses} />
              <button type="button" onClick={() => setShowConfirmPassword(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-black/20 hover:text-[#4A6CF7] transition-colors focus:outline-none">
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {password && confirmPassword && password !== confirmPassword && (
              <p className="text-[10px] font-bold text-red-500 mt-1 ml-1 uppercase tracking-wider">Passwords do not match</p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-md w-full mx-auto">
        {signupError && (
          <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest text-center mb-3">{signupError}</p>
        )}
        <div className="flex items-center gap-3 w-full mt-auto pt-4">
          <button
            onClick={async () => {
              setSignupError('');
              setSignupLoading(true);
              const { data: signUpData, error } = await supabase.auth.signUp({
                email,
                password,
                options: srcParam ? { data: { signup_source: srcParam } } : undefined,
              });
              if (error) {
                setSignupLoading(false);
                setSignupError(error.message);
                return;
              }
              // If Supabase has email confirmation enabled, signUp returns session=null.
              // Immediately sign in to get a real session so the rest of onboarding works.
              if (!signUpData.session) {
                const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
                if (signInError) {
                  setSignupLoading(false);
                  setSignupError('Account created — please check your email to confirm, then log in.');
                  return;
                }
              }
              setSignupLoading(false);
              setStep('PROFILE');
            }}
            disabled={signupLoading || !firstName || !lastName || !email || !password || password !== confirmPassword}
            className={primaryBtnClasses + " w-full flex-none"}
          >
            {signupLoading ? 'Creating account…' : 'Continue'}
          </button>
        </div>
        
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-black/5"></div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#1A1F36]/30">Or continue with</span>
            <div className="h-px flex-1 bg-black/5"></div>
          </div>
          <button
            type="button"
            onClick={async () => {
              await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: { redirectTo: `${window.location.origin}/onboarding` },
              });
            }}
            className="w-full flex items-center justify-center gap-2 py-3 border border-gray-200 rounded-2xl hover:bg-black/5 transition-all"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-4 h-4" alt="Google" />
            <span className="text-[10px] font-black uppercase tracking-widest">Continue with Google</span>
          </button>
        </div>
        <p className="text-center text-[10px] font-bold uppercase tracking-widest text-[#1A1F36]/30 mt-4">
          Already have an account?{' '}
          <button
            type="button"
            onClick={async () => { await supabase.auth.signOut(); navigate('/login'); }}
            className="text-[#4A6CF7] hover:underline"
          >Log in</button>
        </p>
      </div>
    </motion.div>
  );

  const renderProfile = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col h-full"
    >
      <div className="space-y-4">
        <div className="space-y-1 flex flex-col items-start text-left w-full max-w-md mx-auto">
          <div className="flex items-center justify-between w-full">
            <span className="uppercase text-[10px] font-bold tracking-[0.2em] text-[#1A1F36]/50">Step 01 — Your Profile</span>
          </div>
          <h1 className="text-4xl font-black tracking-tight text-[#1A1F36] leading-[0.9]">
            Tell us <br />
            <span className="text-[#4A6CF7]">about you.</span>
          </h1>
        </div>

        <div className="max-w-md space-y-4 mx-auto w-full">
          <div className="group relative">
            <label className={labelClasses}>Your Role / Discipline</label>
            <div className="relative">
              <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
              <input type="text" placeholder="e.g. Animator, Motion Designer, Copywriter" value={role} onChange={e => setRole(e.target.value)} className={inputClasses} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="group relative flex flex-col">
              <label className={labelClasses}>Rate Type</label>
              <div className="flex bg-white/40 border border-black/10 rounded-xl p-1 h-[50px]">
                <button onClick={() => setDefaultRateType('daily')} className={`flex-1 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${defaultRateType === 'daily' ? 'bg-[#4A6CF7] text-white' : 'text-black/40 hover:bg-black/5'}`}>Daily</button>
                <button onClick={() => setDefaultRateType('hourly')} className={`flex-1 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${defaultRateType === 'hourly' ? 'bg-[#4A6CF7] text-white' : 'text-black/40 hover:bg-black/5'}`}>Hourly</button>
              </div>
            </div>
            <div className="group relative flex flex-col gap-2">
              <label className={labelClasses}>Currency</label>
              <div className="flex bg-white/40 border border-black/10 rounded-full p-1 h-[50px]">
                {(['USD', 'GBP', 'EUR'] as const).map(cur => (
                  <button
                    key={cur}
                    type="button"
                    onClick={() => setCurrentJob(prev => ({ ...prev, currency: cur }))}
                    className={`flex-1 rounded-full text-sm font-bold transition-all ${(currentJob.currency || 'USD') === cur ? 'bg-[#4A6CF7] text-white' : 'text-black/40 hover:bg-black/5'}`}
                  >
                    {cur === 'GBP' ? '£' : cur === 'EUR' ? '€' : '$'}
                  </button>
                ))}
              </div>
              <div className="relative">
                <select
                  value={['USD','GBP','EUR'].includes(currentJob.currency || 'USD') ? '' : (currentJob.currency || '')}
                  onChange={e => { if (e.target.value) setCurrentJob(prev => ({ ...prev, currency: e.target.value })); }}
                  className={`w-full pl-3 pr-8 py-2 rounded-2xl border text-xs font-bold appearance-none outline-none transition-all bg-white/40 border-black/10 text-black/50 ${!['USD','GBP','EUR'].includes(currentJob.currency || 'USD') ? 'bg-white text-black border-black/20 shadow-sm' : ''}`}
                >
                  <option value="">Other currency…</option>
                  {([
                    ['NONE','No Currency'],
                    ['AUD','Australian Dollar'],['CAD','Canadian Dollar'],['CHF','Swiss Franc'],
                    ['JPY','Japanese Yen'],['SGD','Singapore Dollar'],['HKD','Hong Kong Dollar'],
                    ['NZD','New Zealand Dollar'],['INR','Indian Rupee'],['AED','UAE Dirham'],
                    ['SAR','Saudi Riyal'],['BRL','Brazilian Real'],['MXN','Mexican Peso'],
                    ['ZAR','South African Rand'],['SEK','Swedish Krona'],['NOK','Norwegian Krone'],
                    ['DKK','Danish Krone'],['PLN','Polish Złoty'],['CZK','Czech Koruna'],
                    ['CNY','Chinese Yuan'],['KRW','South Korean Won'],['TWD','New Taiwan Dollar'],
                    ['THB','Thai Baht'],['TRY','Turkish Lira'],['ILS','Israeli Shekel'],
                  ] as [string, string][]).map(([code, name]) => (
                    <option key={code} value={code}>{getCurrencySymbol(code)} — {name}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-black/30">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
              </div>
            </div>
          </div>
          <div className="group relative">
            <label className={labelClasses}>Default Rate</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-black/30 pointer-events-none">
                {(currentJob.currency || 'USD') === 'GBP' ? '£' : (currentJob.currency || 'USD') === 'EUR' ? '€' : '$'}
              </span>
              <input
                type="number"
                placeholder="0"
                value={defaultRate}
                onChange={e => {
                  const val = e.target.value;
                  const numVal = parseFloat(val) || 0;
                  const autoType = numVal > 200 ? 'daily' : 'hourly';
                  setDefaultRate(val);
                  setDefaultRateType(autoType);
                  setCurrentJob(prev => ({ ...prev, rate: val as any, rateType: autoType }));
                }}
                className={inputClasses + " !pl-8"}
              />
            </div>
          </div>
        </div>
      </div>

      {profileError && (
        <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest text-center mt-2 max-w-md mx-auto">{profileError}</p>
      )}
      <div className="max-w-md w-full mx-auto grid grid-cols-[1fr_auto_1fr] items-center gap-x-8 mt-auto pt-4 min-h-[64px]">
        <div className="flex justify-end">
          <button onClick={() => setStep('SIGNUP')} className={backBtnClasses}><ChevronLeft className="w-3 h-3" /> Back</button>
        </div>
        <button onClick={async () => {
          setProfileError('');
          if (!role) { setProfileError('Please enter your role or discipline.'); return; }
          if (!defaultRate) { setProfileError('Please enter your default rate.'); return; }
          if (!user) { setProfileError('No active session — please refresh and try again.'); return; }
          const { error: upsertErr } = await supabase.from('users').upsert({
            id: user.id,
            email: user.email ?? '',
            first_name: firstName,
            last_name: lastName,
            role_discipline: role,
            default_rate: parseFloat(defaultRate) || 0,
            default_rate_type: defaultRateType,
          }, { onConflict: 'id' });
          if (upsertErr) {
            setProfileError('Could not save your profile. Check your connection and try again.');
            return;
          }
          setStep('CSV_IMPORT');
        }} className={`${primaryBtnClasses} !flex-none px-8 min-w-[180px]`}>Continue</button>
        <div className="flex justify-start" />
      </div>
    </motion.div>
  );

  const isAnyCsvChecked = Object.values(csvContent).some(v => v);

  const renderCsvImport = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col h-full"
    >
      <div className="space-y-4">
        <div className="space-y-1 flex flex-col items-start text-left w-full max-w-md mx-auto">
          <div className="flex items-center justify-between w-full">
            <span className="uppercase text-[10px] font-bold tracking-[0.2em] text-[#1A1F36]/50">Optional — Import Data</span>
          </div>
          <h1 className="text-4xl font-black tracking-tight text-[#1A1F36] leading-[0.9]">
            Import your<br />
            <span className="text-[#4A6CF7]">Booking History.</span>
          </h1>
        </div>

        <div className="w-full max-w-md mx-auto space-y-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/50 text-center">Select what your file contains before uploading.</p>

          <div className="grid grid-cols-2 gap-3">
            {[
              { id: 'currentYear', label: "This Year's Bookings", hint: `Individual jobs from ${new Date().getFullYear()}` },
              { id: 'previousYear', label: "Last Year's Bookings", hint: `Individual jobs from ${new Date().getFullYear() - 1}` },
              { id: 'multipleYears', label: "Other Years", hint: `${new Date().getFullYear() - 2} or any earlier year` },
              { id: 'pastSummary', label: "Annual Sums Only", hint: "Year totals, no individual jobs" }
            ].map(opt => {
              const checked = csvContent[opt.id as keyof typeof csvContent];
              return (
                <button
                  key={opt.id}
                  onClick={() => {
                    // Force single selection (radio button behavior)
                    setCsvContent({
                      currentYear: opt.id === 'currentYear',
                      previousYear: opt.id === 'previousYear',
                      multipleYears: opt.id === 'multipleYears',
                      pastSummary: opt.id === 'pastSummary'
                    });
                    setShowCheckboxError(false);
                  }}
                  className={`flex items-center justify-between gap-3 px-4 py-3 border rounded-xl transition-all text-[10px] font-bold uppercase tracking-wider text-left ${checked ? 'bg-[#4A6CF7]/10 border-[#4A6CF7]/30 text-[#4A6CF7]' : (showCheckboxError ? 'bg-red-50 border-red-400 text-black/40' : 'bg-white/60 border-black/10 text-black/40 hover:bg-black/5')}`}
                >
                  <div className="flex flex-col flex-1">
                    <span>{opt.label}</span>
                    <span className={`text-[9px] font-normal normal-case tracking-normal mt-0.5 ${checked ? 'opacity-70' : 'opacity-50'}`}>{opt.hint}</span>
                  </div>
                  <div className={`shrink-0 w-4 h-4 rounded flex items-center justify-center transition-all ${checked ? 'bg-[#4A6CF7]' : 'border border-[#1A1F36]/30'}`}>
                    {checked && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                </button>
              );
            })}
          </div>

          <div
            onClick={() => { if (isAnyCsvChecked) {
              setShowCheckboxError(false);
              fileInputRef.current?.click();
            } else {
              setShowCheckboxError(true);
            }}}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
            onDrop={e => {
              e.preventDefault();
              setIsDragging(false);
              if (!isAnyCsvChecked) { setShowCheckboxError(true); return; }
              const file = e.dataTransfer.files?.[0];
              if (file) handleCsvUpload({ target: { files: e.dataTransfer.files } } as any);
            }}
            className={`group relative w-full min-h-[180px] border-2 border-dashed rounded-[2rem] flex flex-col items-center justify-center space-y-3 transition-all duration-150 cursor-pointer
              ${isDragging
                ? (isAnyCsvChecked ? 'border-[#4A6CF7] bg-[#4A6CF7]/5' : 'border-red-400 bg-red-50')
                : 'border-gray-200 hover:border-black/30 hover:bg-black/5'
              }`}
          >
            <input type="file" ref={fileInputRef} onChange={handleCsvUpload} className="hidden" accept=".csv,.pdf,.txt,.xlsx,.xls" />
            {isParsing ? (
              <div className="flex flex-col items-center space-y-3">
                <div className="w-10 h-10 border-4 border-[#1A1F36]/30 border-t-[#4A6CF7] rounded-full animate-spin"></div>
                <p className="font-black uppercase text-[10px] tracking-[0.2em] text-[#45556c]">Analyzing Document...</p>
              </div>
            ) : importSuccess ? (
              <div className="flex flex-col items-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center text-white"><Check className="w-6 h-6" /></div>
                <p className="font-black uppercase text-[10px] tracking-[0.2em] text-emerald-600">{importSuccess.count} {importSuccess.type} imported</p>
                {warnings.length > 0 && (
                  <div className="mt-2 px-4 py-2 bg-amber-50 rounded-lg text-left max-w-[280px]">
                    <p className="text-[9px] font-black uppercase tracking-widest text-amber-600 mb-1">Warnings:</p>
                    {warnings.map((w, i) => <p key={i} className="text-[9px] font-medium text-amber-700 leading-tight">• {w}</p>)}
                  </div>
                )}
              </div>
            ) : importError ? (
              <div className="flex flex-col items-center space-y-2 px-6 text-center">
                <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center text-white"><X className="w-6 h-6" /></div>
                <p className="font-black uppercase text-[10px] tracking-[0.2em] text-red-600">{importError}</p>
              </div>
            ) : isDragging ? (
              <p className={`font-black uppercase text-[10px] tracking-[0.2em] text-center px-6 ${isAnyCsvChecked ? 'text-[#4A6CF7]' : 'text-red-500'}`}>
                {isAnyCsvChecked ? 'Drop your file here' : 'Select a data type above before dropping your file'}
              </p>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Upload className="w-5 h-5 text-black" />
                </div>
                <div className="text-center">
                  <p className="font-black uppercase text-[10px] tracking-[0.2em]">Import File (CSV, XLSX, PDF, or TXT)</p>
                  <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-black/30 mt-1">Drag and drop or click to upload</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-md w-full mx-auto grid grid-cols-[1fr_auto_1fr] items-center gap-x-8 mt-auto pt-4 min-h-[64px]">
        <div className="flex justify-end">
          <button onClick={() => {
            if (returnStep) {
              setStep(returnStep);
              setReturnStep(null);
            } else {
              setStep('PROFILE');
            }
          }} className={backBtnClasses}>
            <ChevronLeft className="w-3 h-3" /> Back
          </button>
        </div>

        <button 
          onClick={() => {
            if (isAnyCsvChecked) {
              setShowCheckboxError(false);
              track('csv_upload_attempted');
              fileInputRef.current?.click();
            } else {
              setShowCheckboxError(true);
            }
          }} 
          className={`${primaryBtnClasses} !flex-none px-8 min-w-[180px]`}
        >
          {isParsing ? "Analyzing..." : "Upload File"}
        </button>

        <div className="flex justify-start">
          <button
            onClick={() => { track('csv_skipped'); setStep('PAST_EARNINGS'); }}
            className={backBtnClasses}
          >
            Skip <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </motion.div>
  );

  const renderFirstBooking = () => {
    console.log('Current Job:', currentJob);
    const isImporting = pendingImportedBookings.length > 0;
    const activeBookingId = isImporting ? pendingImportedBookings[0].id : 'new-manual';
    const isFirstBooking = bookedJobs.length === 0;
    const isInvalidClient = currentJob.company === '';
    const isInvalidRate = currentJob.rate === '' || currentJob.rate === '0';
    const isMissingDates = !currentJob.startDate || !currentJob.endDate;
    const currentReasoning = isImporting ? pendingImportedBookings[0].reasoning : null;

    return (
      <motion.div
        key={activeBookingId}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="flex flex-col h-full"
      >
        <div className="space-y-2">
          <div className="space-y-1 flex flex-col items-start text-left w-full max-w-md mx-auto">
            <div className="flex items-center justify-start w-full">
              <div className="flex items-center gap-3">
                {isImporting && (
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#4A6CF7]/10 rounded-full text-[10px] font-black uppercase tracking-widest text-[#4A6CF7] whitespace-nowrap">
                    Booking {totalImportedBookings - pendingImportedBookings.length + 1} of {totalImportedBookings}
                  </div>
                )}
              </div>
            </div>
            <h1 className="text-4xl font-black tracking-tight text-[#1A1F36] leading-[0.9]">
              {isImporting ? "Confirm" : (returnStep === 'PAST_EARNINGS' ? "Log a" : "Your latest")} <br />
              <span className="text-[#4A6CF7]">Booking.</span>
            </h1>
          </div>

          <div className="w-full max-w-md mx-auto space-y-3">
            <div className="group relative">
              <label className={labelClasses}>Client Name</label>
              <div className="relative">
                <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1F36]/30 group-focus-within:text-[#4A6CF7] transition-colors" />
                <input type="text" placeholder="e.g. Google" value={currentJob.company} onChange={e => setCurrentJob({ ...currentJob, company: e.target.value })} className={`${inputClasses} ${isInvalidClient ? 'border-red-300' : ''}`} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="group relative">
                <label className={labelClasses}>Rate Type</label>
                <div className="flex bg-white/60 border border-gray-200 rounded-xl p-1 h-[50px]">
                  <button onClick={() => setCurrentJob({ ...currentJob, rateType: 'daily' })} className={`flex-1 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${currentJob.rateType === 'daily' ? 'bg-[#4A6CF7] text-white' : 'text-[#45556c] hover:bg-black/5'}`}>Daily</button>
                  <button onClick={() => setCurrentJob({ ...currentJob, rateType: 'hourly' })} className={`flex-1 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${currentJob.rateType === 'hourly' ? 'bg-[#4A6CF7] text-white' : 'text-[#45556c] hover:bg-black/5'}`}>Hourly</button>
                </div>
              </div>
              <div className="group relative">
                <label className={labelClasses}>Rate</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-sm font-bold text-[#1A1F36]/30 group-focus-within:text-[#4A6CF7] transition-colors pointer-events-none">
                    {currentJob.currency === 'GBP' ? '£' : currentJob.currency === 'EUR' ? '€' : '$'}
                  </div>
                  <input 
                    type="number" 
                    placeholder="0" 
                    value={currentJob.rate} 
                    onChange={e => { 
                      const val = e.target.value;
                      setCurrentJob({ ...currentJob, rate: val }); 
                      setManualBilled(null); 
                      syncBookingCalculations('rate', { rate: val });
                    }} 
                    className={`${inputClasses} ${isInvalidRate ? 'border-red-300' : ''}`} 
                  />
                </div>
              </div>
            </div>

            {isMissingDates && importedDays !== null ? (
              <div className="group relative">
                <label className={labelClasses}>What year was this booking?</label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1F36]/30 group-focus-within:text-[#4A6CF7] transition-colors" />
                  <input 
                    type="number" 
                    min="2000" 
                    max="2030" 
                    placeholder={new Date().getFullYear().toString()} 
                    value={bookingYear} 
                    onChange={e => {
                      const year = e.target.value;
                      setBookingYear(year);
                      setCurrentJob({ ...currentJob, startDate: `${year}-01-01`, endDate: `${year}-12-31` });
                    }} 
                    className={inputClasses} 
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="group relative">
                  <label className={labelClasses}>Start Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
                    <input 
                      type="date" 
                      value={currentJob.startDate} 
                      onChange={e => { 
                        const val = e.target.value;
                        const updates: any = { startDate: val };
                        
                        // If end date is empty or before start date, sync it
                        if (!currentJob.endDate || currentJob.endDate < val) {
                          updates.endDate = val;
                        }
                        
                        setCurrentJob({ ...currentJob, ...updates }); 
                        setManualBilled(null); 
                        syncBookingCalculations('dates', { start: val, end: updates.endDate || currentJob.endDate });
                      }} 
                      className={dateInputClasses} 
                    />
                  </div>
                </div>
                <div className="group relative">
                  <label className={labelClasses}>End Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
                    <input
                      type="date"
                      value={currentJob.endDate}
                      min={currentJob.startDate || undefined}
                      onFocus={e => {
                        if (!e.target.value && currentJob.startDate) {
                          e.target.value = currentJob.startDate;
                          setTimeout(() => { if (!currentJob.endDate) e.target.value = ''; }, 0);
                        }
                      }}
                      onChange={e => {
                        const val = e.target.value;
                        setCurrentJob({ ...currentJob, endDate: val });
                        setManualBilled(null);
                        syncBookingCalculations('dates', { end: val });
                      }}
                      className={dateInputClasses}
                    />
                  </div>
                </div>
              </div>
            )}

            {importedDays !== null && (
              <div className="grid grid-cols-2 gap-4">
                <div className="group relative flex flex-col">
                  <label className={`${labelClasses} min-h-[34px] flex flex-col justify-end pb-0.5`}>
                    Billed Days
                    <span className="block text-[9px] text-black font-bold uppercase tracking-widest mt-0.5">Edit if needed</span>
                  </label>
                  <div className="relative flex-1">
                    <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
                    <input 
                      type="number" 
                      value={importedDays} 
                      onChange={e => {
                        const newDays = parseFloat(e.target.value) || 0;
                        setImportedDays(newDays);
                        syncBookingCalculations('days', { days: newDays });
                      }} 
                      className={inputClasses} 
                    />
                  </div>
                </div>
                <div className="group relative flex flex-col">
                  <label className={`${labelClasses} min-h-[34px] flex flex-col justify-end pb-0.5`}>
                    Total Billed
                    <span className="block text-[9px] invisible mt-0.5">Placeholder</span>
                  </label>
                  <div className="relative flex-1">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-xs font-bold text-black/20 group-focus-within:text-[#4A6CF7] transition-colors pointer-events-none">
                      {currentJob.currency === 'GBP' ? '£' : currentJob.currency === 'EUR' ? '€' : '$'}
                    </div>
                    <input 
                      type="text" 
                      value={isEarningsFocused ? (importedEarnings ?? '') : (importedEarnings ? new Intl.NumberFormat('en-US').format(importedEarnings) : '')} 
                      onFocus={() => setIsEarningsFocused(true)}
                      onBlur={() => setIsEarningsFocused(false)}
                      onChange={e => {
                        const val = e.target.value.replace(/[^0-9.]/g, '');
                        const newEarnings = parseFloat(val) || 0;
                        setImportedEarnings(newEarnings);
                        setHasManuallyEditedBilled(true);
                        syncBookingCalculations('earnings', { earnings: newEarnings });
                      }} 
                      className={inputClasses} 
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="bg-black/5 rounded-2xl p-3 flex flex-col justify-center min-h-[74px]">
              {(!currentJob.startDate || !currentJob.endDate) && importedEarnings === null ? (
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/30 text-center">Enter dates to see your projection</p>
              ) : (
                <div className="flex flex-col items-center space-y-1">
                  <span className="uppercase text-[10px] font-bold tracking-[0.2em] text-[#1A1F36]/50">Projected Billing</span>
                  <div className="text-3xl font-black text-[#4A6CF7]">
                    {formatCurrency(importedEarnings ?? currentJobStats.billed, currentJob.currency)}
                  </div>
                  <div className="text-[10px] font-bold text-[#45556c] uppercase tracking-widest">
                    {importedDays ?? currentJobStats.days} Days
                  </div>
                </div>
              )}
            </div>

            {/* Smart Import Info removed */}
          </div>
        </div>

        <div className="max-w-md w-full mx-auto grid grid-cols-[1fr_auto_1fr] items-center gap-x-8 mt-auto pt-3 min-h-[58px]">
          <div className="flex justify-end">
            <button 
              onClick={() => {
                setImportSuccess(null);
                setImportError(null);
                setWarnings([]);
                setCsvImportMode(false);
                setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
                setImportedDays(null);
                setImportedEarnings(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                setStep('CSV_IMPORT');
              }} 
              className={backBtnClasses}
            >
              <ChevronLeft className="w-3 h-3" /> Back
            </button>
          </div>

          <button 
            onClick={handleAddBooking} 
            disabled={!currentJob.company || !currentJob.rate} 
            className={`${primaryBtnClasses} !flex-none px-8 min-w-[180px]`}
          >
            {isImporting ? "Confirm" : "Add Booking"}
          </button>

          <div className="flex justify-start">
            {!isFirstBooking && !isImporting && (
              <button onClick={() => setStep('PAST_EARNINGS')} className={backBtnClasses}>
                Skip <ChevronRight className="w-3 h-3" />
              </button>
            )}
            {isImporting && (
              <button 
                onClick={() => handleBulkConfirm()} 
                className={`${backBtnClasses} whitespace-nowrap`}
              >
                Confirm All <ChevronRight className="w-3 h-3 ml-1" />
              </button>
            )}
            {isFirstBooking && !isImporting && <div className="w-[40px]" />}
          </div>
        </div>
      </motion.div>
    );
  };

  const renderAddMoreLoop = () => {
    const lastBooking = bookedJobs[bookedJobs.length - 1];
    const latestYearStr = lastBooking?.startDate?.split('-')[0];
    
    // Only show summary for the year of the most recently added booking
    const relevantJobs = latestYearStr 
      ? bookedJobs.filter(j => j.startDate.startsWith(latestYearStr))
      : bookedJobs;

    const isBulkAvailable = pendingImportedBookings.length > 1;

    const uniqueCompanies = Array.from(new Set(relevantJobs.map(j => j.company).filter(Boolean)));
    let companyList = uniqueCompanies.join(', ');
    if (companyList.length > 40) {
      companyList = companyList.substring(0, 37) + '...';
    }
    const totalEarningsAggregate = relevantJobs.reduce((sum, j) => sum + (j.earnings || 0), 0);
    const totalDaysAggregate = relevantJobs.reduce((sum, j) => sum + (j.days || 0), 0);
    const bookingsCount = relevantJobs.length;

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 1.05 }}
        className="flex flex-col h-full"
      >
        <div className="flex flex-col items-center space-y-4 text-center">
          <div className="w-14 h-14 rounded-full bg-black flex items-center justify-center shadow-xl"><Check className="w-7 h-7 text-white" /></div>
          <h2 className="text-3xl font-black tracking-tight text-[#1A1F36]">
            {latestYearStr && <span className="block text-xl mb-0.5 text-[#4A6CF7]">{latestYearStr}</span>}
            {bookedJobs.length > 1 ? "Bookings" : "Booking"} <br/>
            <span className="text-[#4A6CF7]">{bookedJobs.length > 1 ? "Updated." : "Added."}</span>
          </h2>
          
          {showBulkConfirm ? (
            <div className="bg-black/5 rounded-2xl p-4 space-y-3 w-full max-w-sm">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#1A1F36]/50 leading-relaxed">
                You're about to confirm {pendingImportedBookings.length} bookings without reviewing each one. Some may have incomplete data. Are you sure?
              </p>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowBulkConfirm(false)} className="flex-1 py-3 border border-gray-200 rounded-xl font-black uppercase text-[10px] tracking-widest text-[#45556c] hover:bg-black/5 transition-all">Go Back</button>
                <button onClick={handleBulkConfirm} className="flex-1 py-3 bg-[#E3E8F1] text-[#45556c] rounded-full font-black uppercase text-[10px] tracking-widest transition-all hover:bg-black hover:text-white">Confirm All</button>
              </div>
            </div>
          ) : (
            <div className="bg-black/5 rounded-2xl p-5 space-y-4 w-full max-w-sm text-left">
              {bookedJobs.length > 1 ? (
                <>
                  <div className="space-y-1">
                    <label className={labelClasses + " ml-0"}>Clients</label>
                    <div className="font-black text-xl text-[#1A1F36]">{companyList}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className={labelClasses + " ml-0"}>TOTAL BILLED SO FAR</label>
                      <div className="font-black text-2xl text-[#4A6CF7]">{formatCurrency(totalEarningsAggregate, bookedJobs[0]?.currency)}</div>
                    </div>
                    <div className="space-y-1">
                      <label className={labelClasses + " ml-0"}>TOTAL DAYS</label>
                      <div className="font-black text-2xl text-[#1A1F36]/50">{totalDaysAggregate} days</div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-black/5">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#1A1F36]/50">BOOKINGS CONFIRMED: {bookingsCount}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-1">
                    <label className={labelClasses + " ml-0"}>Client Name</label>
                    <div className="font-black text-xl text-[#1A1F36]">{lastBooking?.company}</div>
                  </div>
                  <div className="space-y-1">
                    <label className={labelClasses + " ml-0"}>Date Range</label>
                    <div className="font-black text-base text-[#1A1F36]">{formatDateRange(lastBooking?.startDate, lastBooking?.endDate)}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className={labelClasses + " ml-0"}>Total Billed</label>
                      <div className="font-black text-2xl text-[#4A6CF7]">{formatCurrency(lastBooking?.earnings, lastBooking?.currency)}</div>
                    </div>
                    <div className="space-y-1">
                      <label className={labelClasses + " ml-0"}>Duration</label>
                      <div className="font-black text-2xl text-[#1A1F36]/50">{lastBooking?.days} days</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="mt-auto pt-4 space-y-3 w-full max-w-md mx-auto">
          {isBulkAvailable && !showBulkConfirm && (
            <button 
              onClick={() => setShowBulkConfirm(true)} 
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#E3E8F1] rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-[#45556c] hover:bg-black hover:text-white transition-all mx-auto"
            >
              Confirm All <Check className="w-3 h-3" />
            </button>
          )}
          {!showBulkConfirm && (
            <div className="flex items-center gap-3 w-full">
              <button 
                onClick={() => { 
                  if (returnStep === 'PAST_EARNINGS') {
                    setStep('FIRST_BOOKING');
                  } else if (csvImportMode) { 
                    setImportSuccess(null);
                    setImportError(null);
                    setWarnings([]);
                    setCsvImportMode(false);
                    setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
                    setImportedDays(null);
                    setImportedEarnings(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    setStep('CSV_IMPORT'); 
                  } else { 
                    setStep('FIRST_BOOKING'); 
                  } 
                }} 
                className={backBtnClasses + " border border-gray-200 justify-center"}
              >
                Add Another <Plus className="w-3 h-3" />
              </button>
              <button onClick={() => setStep('PAST_EARNINGS')} className={primaryBtnClasses + " flex items-center justify-center gap-2"}>
                Continue <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const renderPastEarnings = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col h-full"
    >
      <>
      <div className="flex flex-col flex-1 overflow-hidden space-y-4">
        <div className="space-y-1 flex flex-col items-start text-left w-full max-w-md mx-auto">
          <div className="flex items-center justify-between w-full">
            <span className="uppercase text-[10px] font-bold tracking-[0.2em] text-[#1A1F36]/50">Step 03 — Historical Data</span>
          </div>
          <h1 className="text-4xl font-black tracking-tight text-[#1A1F36] leading-[0.9]">
            Booking <br />
            <span className="text-[#4A6CF7]">History.</span>
          </h1>
        </div>

        <div className="flex flex-col flex-1 overflow-hidden space-y-3 max-w-md mx-auto w-full">
            <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scrollbar">
              {performanceHistory.map((item, idx) => (
                <React.Fragment key={idx}>
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white/60 border border-white rounded-xl p-3 flex items-center gap-4">
                  <div className="w-20">
                    <label className="block text-[9px] font-bold uppercase tracking-widest text-[#1A1F36]/50 mb-1">Year</label>
                    <input type="text" value={item.year} onChange={e => updateYearData(idx, 'year', e.target.value)} className="w-full bg-transparent font-black text-base focus:outline-none text-[#1A1F36]" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[9px] font-bold uppercase tracking-widest text-[#1A1F36]/50 mb-1">Total Earned</label>
                    <div className="flex items-center gap-1">
                      <span className="font-black text-[#1A1F36]/30 text-sm">{currentJob.currency === 'GBP' ? '£' : currentJob.currency === 'EUR' ? '€' : '$'}</span>
                      <input type="text" value={item.totalEarned === 0 ? '' : new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(item.totalEarned)} onChange={e => updateYearData(idx, 'totalEarned', e.target.value)} placeholder="0" className="w-full bg-transparent font-black text-base focus:outline-none text-[#1A1F36]" />
                    </div>
                  </div>
                  <div className="w-24">
                    <label className="block text-[9px] font-bold uppercase tracking-widest text-[#1A1F36]/50 mb-1">Days worked</label>
                    <input type="number" value={item.totalDaysWorked} onChange={e => updateYearData(idx, 'totalDaysWorked', parseInt(e.target.value))} className="w-full bg-transparent font-black text-base focus:outline-none text-[#1A1F36]" />
                  </div>
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={() => handleAddDetailedBooking(item.year)}
                      className="p-2 hover:bg-[#4A6CF7]/10 rounded-full transition-colors group"
                      title="Add individual bookings for this year"
                    >
                      <PlusCircle className="w-4 h-4 text-[#4A6CF7] opacity-40 group-hover:opacity-100" />
                    </button>
                    <button onClick={() => setPerformanceHistory(prev => prev.filter((_, i) => i !== idx))} className="p-2 hover:bg-red-50 rounded-full transition-colors group"><X className="w-4 h-4 text-red-300 group-hover:text-red-500" /></button>
                  </div>
                </motion.div>
              </React.Fragment>
            ))}

            {performanceHistory.length === 0 && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                className="py-14 px-8 text-center space-y-6 bg-white/40 border border-white/60 rounded-[2.5rem] shadow-sm w-full"
              >
                <div className="space-y-2">
                  <p className="text-2xl font-black text-[#1A1F36]">No booking history yet.</p>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/40 max-w-[280px] mx-auto leading-relaxed">
                    Add some to get the most out of FirstHold.
                  </p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowManualEntryChoice(true)}
                    className="flex-1 py-5 border-2 border-dashed border-[#E3E8F1] rounded-2xl flex flex-col items-center justify-center gap-2 hover:border-[#1A1F36]/20 bg-white/50 transition-all group"
                  >
                    <Plus className="w-5 h-5 text-[#1A1F36]/20 group-hover:text-[#1A1F36] transition-colors" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#1A1F36]/40 group-hover:text-[#1A1F36]">Add Manually</span>
                  </button>
                  <button
                    onClick={() => {
                      setReturnStep('PAST_EARNINGS');
                      setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
                      setImportSuccess(null);
                      setImportError(null);
                      setWarnings([]);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                      setStep('CSV_IMPORT');
                    }}
                    className="flex-1 py-5 border-2 border-dashed border-[#4A6CF7]/10 rounded-2xl flex flex-col items-center justify-center gap-2 hover:bg-[#4A6CF7]/5 hover:border-[#4A6CF7]/30 bg-white/50 transition-all group"
                  >
                    <Upload className="w-5 h-5 text-[#4A6CF7]/20 group-hover:text-[#4A6CF7] transition-colors" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#4A6CF7]/40 group-hover:text-[#4A6CF7]">Import Files</span>
                  </button>
                </div>
              </motion.div>
            )}
            </div>

            {performanceHistory.length > 0 && (
              <div className="pt-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="flex items-center gap-4 py-3 w-full">
                  <div className="h-[1px] flex-1 bg-[#E3E8F1]"></div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#1A1F36]/30 whitespace-nowrap">Add Bookings</span>
                  <div className="h-[1px] flex-1 bg-[#E3E8F1]"></div>
                </div>
                
                <div className="flex gap-3">
                  <button onClick={() => setShowManualEntryChoice(true)} className="flex-1 py-4 border-2 border-dashed border-[#E3E8F1] rounded-2xl flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#1A1F36]/50 hover:border-gray-200 hover:text-black transition-all">Add Manual Row <Plus className="w-3 h-3" /></button>
                  <button 
                    onClick={() => {
                      setReturnStep('PAST_EARNINGS');
                      setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
                      setImportSuccess(null);
                      setImportError(null);
                      setWarnings([]);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                      setStep('CSV_IMPORT');
                    }} 
                    disabled={isParsing}
                    className="flex-1 py-4 border-2 border-dashed border-[#4A6CF7]/10 rounded-2xl flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#4A6CF7]/60 hover:bg-[#4A6CF7]/5 hover:border-[#4A6CF7]/30 transition-all disabled:opacity-50"
                  >
                    {isParsing ? "Analyzing..." : <>Import Files <Upload className="w-3 h-3" /></>}
                  </button>
                </div>
              </div>
            )}

            {importError && step === 'PAST_EARNINGS' && (
              <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-center">
                <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest">{importError}</p>
              </div>
            )}
        </div>
      </div>

      <div className="max-w-md w-full mx-auto grid grid-cols-[1fr_auto_1fr] items-center gap-x-8 mt-auto pt-4 min-h-[64px]">
        <div className="flex justify-end">
          <button onClick={() => {
            setReturnStep(null);
            setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
            setImportSuccess(null);
            setImportError(null);
            setStep('CSV_IMPORT');
          }} className={backBtnClasses}>
            <ChevronLeft className="w-3 h-3" /> Back
          </button>
        </div>

        <button 
          onClick={() => setStep('GOALS')} 
          className={`${primaryBtnClasses} !flex-none px-8 min-w-[180px]`}
        >
          Continue
        </button>

        <div className="flex justify-start">
          {performanceHistory.length === 0 && (
            <button 
              onClick={() => setStep('GOALS')} 
              className={backBtnClasses}
            >
              Skip <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showManualEntryChoice && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm overflow-y-auto [@supports_not_(backdrop-filter:blur(1px))]:bg-black/80">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-[2rem] p-6 w-full max-w-[95vw] sm:max-w-sm shadow-2xl space-y-5"
            >
              <div className="space-y-1.5">
                <h3 className="text-xl font-black tracking-tight text-[#1A1F36]">How would you like to add?</h3>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#1A1F36]/40">Choose an entry method</p>
              </div>
              
              <div className="space-y-3">
                <button 
                  onClick={() => {
                    handleAddYear();
                    setShowManualEntryChoice(false);
                  }}
                  className="w-full p-4 rounded-2xl border border-gray-100 bg-white hover:bg-black/5 transition-all text-left flex items-center gap-4 group"
                >
                  <div className="w-10 h-10 rounded-full bg-black/5 flex items-center justify-center group-hover:bg-black group-hover:text-white transition-colors">
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-black text-sm text-[#1A1F36]">Quick Annual Entry</p>
                    <p className="text-[10px] font-medium text-[#1A1F36]/50 uppercase tracking-widest">Total earnings and days for the year</p>
                  </div>
                </button>

                <button 
                  onClick={() => {
                    const lastYear = performanceHistory.length > 0
                      ? (parseInt(performanceHistory[performanceHistory.length - 1].year) - 1).toString()
                      : new Date().getFullYear().toString();
                    handleAddDetailedBooking(lastYear);
                  }}
                  className="w-full p-4 rounded-2xl border border-[#4A6CF7]/20 bg-[#4A6CF7]/5 hover:bg-[#4A6CF7]/10 transition-all text-left flex items-center gap-4 group"
                >
                  <div className="w-10 h-10 rounded-full bg-[#4A6CF7] flex items-center justify-center text-white">
                    <FileStack className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-black text-sm text-[#4A6CF7]">Job by Job</p>
                    <p className="text-[10px] font-medium text-[#4A6CF7]/60 uppercase tracking-widest">Every booking, logged individually</p>
                  </div>
                </button>
              </div>

              <button 
                onClick={() => setShowManualEntryChoice(false)}
                className="w-full py-3 text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A1F36]/30 hover:text-black transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      </>
    </motion.div>
  );

  // Saves all onboarding data: profile, bookings, yearly_performance, subscription.
  // Called when the user finishes GOALS (either via SECURE_TRIAL button or directly when
  // ENABLE_STRIPE_ONBOARDING = false). Fire-and-forget — caller shows COMPLETE screen first.
  const handleCompletionSave = async () => {
    let freshUser: { id: string; email?: string } | null = null;
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      freshUser = currentSession?.user ?? null;

      if (!freshUser || !currentSession?.access_token) {
        console.error('[StartFreeTrial] No session found — cannot save data.');
        markOnboardingComplete();
        setTimeout(() => navigate('/'), 1500);
        return;
      }
      const user = freshUser;

      // 1. Save full profile + goal
      const { error: goalErr } = await supabase.from('users').upsert({
        id: user.id,
        email: user.email ?? '',
        annual_goal: annualGoal ? parseFloat(annualGoal) || 0 : null,
        first_name: firstName || null,
        last_name: lastName || null,
        role_discipline: role || null,
        default_rate: parseFloat(defaultRate) || null,
        default_rate_type: defaultRateType,
      }, { onConflict: 'id' });
      if (goalErr) console.error('[StartFreeTrial] users upsert error:', goalErr);

      // 2. Call create-subscription edge function
      try {
        const fnRes = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-subscription`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${currentSession.access_token}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ plan: selectedPlan }),
          }
        );
        const fnJson = await fnRes.json().catch(() => ({}));
        if (!fnRes.ok) {
          console.error('[StartFreeTrial] create-subscription error:', fnJson);
        } else {
          console.log('[StartFreeTrial] create-subscription ok:', fnJson);
        }
      } catch (fnErr) {
        console.error('[StartFreeTrial] create-subscription fetch threw:', fnErr);
      }

      // 3. Client-side fallback: set onboarding_complete=true directly
      const { error: ocErr } = await supabase.from('users').upsert(
        { id: user.id, email: user.email ?? '', onboarding_complete: true },
        { onConflict: 'id' }
      );
      if (ocErr) console.error('[StartFreeTrial] onboarding_complete fallback error:', ocErr);

      // Save currency preference to app_settings so the dashboard picks it up immediately
      const { error: currErr } = await supabase.from('app_settings').upsert(
        { user_id: user.id, currency: currentJob.currency || 'USD' },
        { onConflict: 'user_id' }
      );
      if (currErr) console.error('[StartFreeTrial] app_settings currency upsert error:', currErr);

      // 4. Batch save individual bookings entered during onboarding
      if (bookedJobs.length > 0) {
        const { error: bookErr } = await supabase.from('bookings').insert(
          bookedJobs.map(job => ({
            user_id: user.id,
            company: job.company,
            role: job.role || '',
            start_date: job.startDate,
            end_date: job.endDate,
            days: job.days,
            rate: job.rate,
            rate_type: job.rateType,
            hours_per_day: job.hoursPerDay ?? 8,
            earnings: isNaN(job.earnings) ? 0 : job.earnings,
            status: job.status,
            currency: job.currency || 'USD',
            import_source: job.importSource || 'manual',
          }))
        );
        if (bookErr) console.error('[StartFreeTrial] bookings insert error:', bookErr);
      }

      // 5. Upsert yearly_performance history
      // Current year: recalculate from started bookings only (startDate <= today).
      // Past years: use performanceHistory state (entered manually or via CSV).
      {
        const currentYr = new Date().getFullYear();
        const n = new Date();
        const todayStr = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;

        const startedCurrentYrJobs = bookedJobs.filter(
          j => j.startDate && j.startDate.startsWith(String(currentYr)) && j.startDate <= todayStr
        );
        const currentYrEarned = startedCurrentYrJobs.reduce((s, j) => s + (isNaN(j.earnings) ? 0 : j.earnings), 0);
        const currentYrDays = startedCurrentYrJobs.reduce((s, j) => s + (j.days || 0), 0);
        const currentYrAvgRate = currentYrDays > 0 ? currentYrEarned / currentYrDays : 0;

        const perfRows: {
          user_id: string; year: number; total_earned: number;
          total_days_worked: number; avg_day_rate: number; currency: string; import_source: string;
        }[] = [];

        if (startedCurrentYrJobs.length > 0) {
          perfRows.push({
            user_id: user.id,
            year: currentYr,
            total_earned: currentYrEarned,
            total_days_worked: currentYrDays,
            avg_day_rate: currentYrAvgRate,
            currency: 'USD',
            import_source: 'manual',
          });
        }

        performanceHistory
          .filter(p => parseInt(p.year) !== currentYr)
          .forEach(p => {
            perfRows.push({
              user_id: user.id,
              year: parseInt(p.year),
              total_earned: p.totalEarned,
              total_days_worked: p.totalDaysWorked,
              avg_day_rate: p.avgDayRate,
              currency: 'USD',
              import_source: 'manual',
            });
          });

        if (perfRows.length > 0) {
          const { error: perfErr } = await supabase.from('yearly_performance').upsert(
            perfRows,
            { onConflict: 'user_id,year' }
          );
          if (perfErr) console.error('[StartFreeTrial] yearly_performance upsert error:', perfErr);
        }
      }

      // Fire welcome email via Loops — fire-and-forget, never blocks navigation.
      // Refresh session first so the token is guaranteed fresh after all the async saves.
      if (user.email) {
        const { data: { session: freshSession } } = await supabase.auth.refreshSession();
        const token = freshSession?.access_token ?? currentSession.access_token;
        fetch('/api/loops?action=welcome', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            email: user.email,
            firstName: firstName || undefined,
            lastName:  lastName  || undefined,
          }),
        }).then(async r => {
          if (!r.ok) {
            const body = await r.text().catch(() => '');
            console.error('[welcome] Loops API error:', r.status, body);
            Sentry.captureException(new Error(`Welcome email failed for ${user.email}: ${r.status} ${body}`), {
              tags: { route: 'onboarding', action: 'welcome' },
              extra: { email: user.email, status: r.status },
            });
          }
        }).catch(err => {
          console.error('[welcome] fetch error:', err);
          Sentry.captureException(err, {
            tags: { route: 'onboarding', action: 'welcome' },
            extra: { email: user.email },
          });
        });
      }
    } catch (err) {
      console.error('[StartFreeTrial] Unexpected error:', err);
    }

    markOnboardingComplete();
    setTimeout(() => navigate('/'), 1500);
  };

  const renderGoals = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col h-full"
    >
      <div className="space-y-4">
        <div className="space-y-1 flex flex-col items-start text-left w-full max-w-md mx-auto">
          <div className="flex items-center justify-between w-full">
            <span className="uppercase text-[10px] font-bold tracking-[0.25em] text-[#1A1F36]/50">Final Step — Smart Benchmarking</span>
          </div>
          <h1 className="text-4xl font-black tracking-tight text-[#1A1F36] leading-[0.9]">Future <br /><span className="text-[#4A6CF7]">Goals.</span></h1>
        </div>

        <div className="w-full max-w-md mx-auto space-y-4">
          <div className="group relative">
            <label className="block uppercase text-[10px] font-bold tracking-[0.25em] text-[#45556c] mb-2 ml-1">Annual Income Goal</label>
            <div className="relative">
              <Target className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1F36]/30 group-focus-within:text-black transition-colors" />
              <input 
                type="text" 
                value={isGoalFocused ? (annualGoal ? annualGoal.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : '') : (annualGoal ? formatCurrency(parseFloat(annualGoal), currentJob.currency) : '')} 
                onFocus={() => setIsGoalFocused(true)}
                onBlur={() => setIsGoalFocused(false)}
                onChange={e => { 
                  const val = e.target.value.replace(/[^0-9.]/g, ''); 
                  setAnnualGoal(val); 
                }} 
                placeholder="What's your target?" 
                className={inputClasses} 
              />
            </div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#1A1F36]/50 mt-2 ml-1 leading-relaxed">Set a target to track your progress. You can update this anytime.</p>
          </div>
        </div>
      </div>

      <div className="max-w-md w-full mx-auto grid grid-cols-[1fr_auto_1fr] items-center gap-x-8 mt-auto pt-4 min-h-[64px]">
        <div className="flex justify-end">
          <button onClick={() => setStep('PAST_EARNINGS')} className={backBtnClasses}><ChevronLeft className="w-3 h-3" /> Back</button>
        </div>
        <button onClick={() => {
          if (ENABLE_STRIPE_ONBOARDING) {
            // Phase 5B: show payment step before completing
            if (user && annualGoal) {
              supabase.from('users').upsert({ id: user.id, email: user.email ?? '', annual_goal: parseFloat(annualGoal) || 0 }, { onConflict: 'id' });
            }
            setStep('SECURE_TRIAL');
          } else {
            // Trial is free — skip payment step, go straight to dashboard
            setStep('COMPLETE');
            handleCompletionSave();
          }
        }} className={`${primaryBtnClasses} !flex-none px-8 min-w-[180px]`}>Continue →</button>
        <div className="flex justify-start" />
      </div>
    </motion.div>
  );

  const renderSecureTrial = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col h-full"
    >
      <div className="space-y-4">
        <div className="space-y-1 flex flex-col items-start text-left w-full max-w-md mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-black text-white rounded-full w-fit">
            <Clock className="w-3 h-3" />
            <span className="text-[10px] font-black uppercase tracking-widest">14-Day Free Trial Active</span>
          </div>
          <h1 className="text-4xl font-black tracking-tight text-[#1A1F36] leading-[0.9]">Your Command Center <br /><span className="text-[#4A6CF7]">is ready.</span></h1>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#45556c]">Secure your free trial to access it.</p>
          <p className="text-[10px] font-bold text-[#4A6CF7] uppercase tracking-[0.2em]">Trial ends {trialEndDate} — no charge today. Cancel any time.</p>
        </div>

        <div className="w-full max-w-md mx-auto space-y-4">
          {/* Plan selector button */}
          <button onClick={() => setShowPlanModal(true)} className="w-full bg-[#E3E8F1] rounded-2xl p-5 text-left transition-all hover:bg-[#D8DFE9] active:scale-[0.99]">
            <div className="flex items-center justify-between">
              <div className="space-y-1.5">
                <p className="font-black text-[#1A1F36] text-lg leading-none">
                  {selectedPlan === 'lead' ? 'Lead Plan' : 'Junior Plan'}
                </p>
                <p className="text-[#4A6CF7] font-bold text-sm">
                  {selectedPlan === 'lead' ? '$9.50 / month' : '$5.50 / month'}
                </p>
              </div>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#4A6CF7] whitespace-nowrap ml-4">Change →</span>
            </div>
          </button>

{/* Plan modal rendered at root level — see return() */}

          {/* Stripe payment fields — placeholder until Phase 5 billing is wired */}
          <div className="bg-[#E3E8F1] rounded-2xl p-6 space-y-4">
            <div className="group relative">
              <label className="block uppercase text-[10px] font-bold tracking-[0.2em] text-[#45556c] mb-1.5 ml-1">Card Number</label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#1A1F36]/30 group-focus-within:text-black transition-colors" />
                <input type="text" placeholder="4242 4242 4242 4242" className="w-full bg-white/60 border border-gray-200 rounded-xl py-3 pl-10 pr-3 text-base font-bold text-[#1A1F36] focus:outline-none focus:border-black/20 focus:bg-white/60 transition-all" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="group relative">
                <label className="block uppercase text-[10px] font-bold tracking-[0.2em] text-[#45556c] mb-1.5 ml-1">Expiry</label>
                <input type="text" placeholder="MM / YY" className="w-full bg-white/60 border border-gray-200 rounded-xl py-3 px-3 text-base font-bold text-[#1A1F36] focus:outline-none focus:border-black/20 focus:bg-white/60 transition-all" />
              </div>
              <div className="group relative">
                <label className="block uppercase text-[10px] font-bold tracking-[0.2em] text-[#45556c] mb-1.5 ml-1">CVC</label>
                <input type="text" placeholder="CVC" className="w-full bg-white/60 border border-gray-200 rounded-xl py-3 px-3 text-base font-bold text-[#1A1F36] focus:outline-none focus:border-black/20 focus:bg-white/60 transition-all" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <span className="text-[8px] font-bold text-[#1A1F36]/50 uppercase tracking-widest">Secured by Stripe</span>
              <span className="text-[8px] font-bold text-amber-500 uppercase tracking-widest">Dev mode — no charge</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-md w-full mx-auto grid grid-cols-[1fr_auto_1fr] items-center gap-x-8 mt-auto pt-4 min-h-[64px]">
        <div className="flex justify-end">
          <button onClick={() => setStep('GOALS')} className={backBtnClasses}><ChevronLeft className="w-3 h-3" /> Back</button>
        </div>
        <button
          onClick={() => {
            setStep('COMPLETE');
            handleCompletionSave(); // shared save logic — same as GOALS path when flag is off
          }}
          className={`${primaryBtnClasses} !flex-none px-8 min-w-[180px]`}
        >Start Free Trial →</button>
        <div className="flex justify-start" />
      </div>
    </motion.div>
  );

  const renderComplete = () => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex-1 flex flex-col items-center justify-center space-y-6 text-center">
      <div className="w-20 h-20 rounded-full bg-[#4A6CF7] flex items-center justify-center shadow-xl animate-bounce shadow-[#4A6CF7]/20"><TrendingUp className="w-8 h-8 text-white" /></div>
      <div className="space-y-2">
        <h1 className="text-5xl font-black tracking-tighter text-[#1A1F36]">Ready.</h1>
        <p className="text-[10px] font-bold uppercase tracking-[0.5em] text-[#1A1F36]/30">Taking you to your dashboard...</p>
      </div>
    </motion.div>
  );

  const planModalNode = (
    <AnimatePresence>
      {showPlanModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-6 backdrop-blur-xl bg-white/20 overflow-y-auto [@supports_not_(backdrop-filter:blur(1px))]:bg-white/90"
          onClick={() => setShowPlanModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            className="bg-white rounded-[2.5rem] p-4 sm:p-6 md:p-10 w-full max-w-[95vw] lg:max-w-2xl shadow-2xl relative"
            onClick={e => e.stopPropagation()}
          >
            <button onClick={() => setShowPlanModal(false)} className="absolute top-6 right-6 w-9 h-9 rounded-xl flex items-center justify-center text-[#1A1F36]/30 hover:text-[#1A1F36] hover:bg-black/5 transition-all hover:scale-110 active:scale-95">
              <X className="w-5 h-5" />
            </button>
            <div className="mb-6">
              <h2 className="text-3xl font-black text-[#1A1F36] tracking-tight mb-1">Choose your plan.</h2>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/40">Free for 14 days, no matter which you pick.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {([
                {
                  key: 'lead' as const,
                  label: 'Lead',
                  price: '$9.50',
                  features: ['Everything in Junior', 'Estimated Taxes & Payouts', 'Advanced Analytics', 'Invoice Management'],
                },
                {
                  key: 'junior' as const,
                  label: 'Junior',
                  price: '$5.50',
                  features: ['Booking Calendar', 'Hold Tracking', 'Income Dashboard'],
                },
              ]).map(plan => {
                const isSelected = selectedPlan === plan.key;
                return (
                  <button
                    key={plan.key}
                    onClick={() => { setSelectedPlan(plan.key); setShowPlanModal(false); }}
                    className={`w-full text-left rounded-[2rem] border-2 p-6 transition-all ${isSelected ? 'border-[#4A6CF7] bg-[#4A6CF7]/5' : 'border-gray-100 bg-gray-50/50 hover:border-gray-200'}`}
                  >
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#4A6CF7] mb-2">{plan.label} Plan</p>
                    <p className="text-3xl font-black text-[#1A1F36] mb-0.5">{plan.price}<span className="text-sm font-bold text-[#1A1F36]/40 ml-1">/mo</span></p>
                    <div className="mt-4 space-y-2">
                      {plan.features.map(f => (
                        <div key={f} className="flex items-center gap-2">
                          <Check className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? 'text-[#4A6CF7]' : 'text-gray-300'}`} />
                          <span className="text-[10px] font-bold uppercase tracking-widest text-[#1A1F36]/60">{f}</span>
                        </div>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="min-h-screen w-full bg-[hsl(225,30%,92%)] flex items-center justify-center p-6 font-sans selection:bg-black/10">
      <div className="w-full max-w-[1400px] flex flex-col items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: prefersReducedMotion ? 0 : 0.5 }} className="relative w-full max-w-2xl bg-white/75 backdrop-blur-[28px] border border-white/65 shadow-xl shadow-blue-900/5 overflow-hidden rounded-[2rem] [@supports_not_(backdrop-filter:blur(1px))]:bg-white">
          <div className="absolute top-0 left-0 w-full h-1 bg-[#E3E8F1]">
            <motion.div className="h-full bg-[#4A6CF7]" initial={{ width: '0%' }} animate={{ width: step === 'SIGNUP' ? '12%' : step === 'PROFILE' ? '25%' : step === 'CSV_IMPORT' ? '37%' : step === 'FIRST_BOOKING' ? '50%' : step === 'ADD_MORE_LOOP' ? '60%' : step === 'PAST_EARNINGS' ? '72%' : step === 'GOALS' ? (ENABLE_STRIPE_ONBOARDING ? '85%' : '92%') : step === 'SECURE_TRIAL' ? '95%' : '100%' }} transition={{ type: 'spring', stiffness: 100, damping: 20 }} />
          </div>
          {(['CSV_IMPORT', 'FIRST_BOOKING', 'ADD_MORE_LOOP', 'PAST_EARNINGS', 'GOALS'] as OnboardingStep[]).includes(step) && !isParsing && (
            <div className="absolute top-5 right-5 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor" className="text-emerald-500 flex-shrink-0"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>
              <span className="text-[9px] font-black uppercase tracking-[0.15em] text-emerald-600">Encrypted</span>
            </div>
          )}
          <div className="p-6 md:p-10 h-[680px] flex flex-col overflow-hidden">
            <AnimatePresence mode="wait">
              {isParsing ? (
                <motion.div key="parsing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col items-center justify-center space-y-6">
                  <div className="w-16 h-16 border-4 border-gray-200 border-t-[#4A6CF7] rounded-full animate-spin" />
                  <div className="text-center space-y-2 px-8">
                    <p className="font-black uppercase text-xs tracking-[0.3em]">Analyzing Booking Data</p>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/50">
                      {isSweating ? (
                        <span className={`text-amber-600 ${prefersReducedMotion ? '' : 'animate-pulse'}`}>It must be a big file, because Im sweating...</span>
                      ) : isTakingLonger ? (
                        <span className={`text-[#4A6CF7] ${prefersReducedMotion ? '' : 'animate-pulse'}`}>Its taking longer than usual, give us an extra minute</span>
                      ) : (
                        "This won't take long..."
                      )}
                    </p>
                    <p className="text-[9px] font-black tabular-nums text-[#1A1F36]/20 uppercase tracking-widest mt-1">
                      Running for {elapsedSeconds} SECONDS
                    </p>
                  </div>

                  <div className="w-full aspect-video rounded-[2.5rem] overflow-hidden shadow-2xl relative bg-black mt-10">
                    <video 
                      key={loadingVideos[videoIndex]}
                      src={loadingVideos[videoIndex]}
                      autoPlay 
                      loop 
                      muted 
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  </div>
                </motion.div>
              ) : (
                <>
                  {step === 'SIGNUP' && renderSignup()}
                  {step === 'PROFILE' && renderProfile()}
                  {step === 'CSV_IMPORT' && renderCsvImport()}
                  {step === 'FIRST_BOOKING' && renderFirstBooking()}
                  {step === 'PAST_EARNINGS' && renderPastEarnings()}
                  {step === 'ADD_MORE_LOOP' && renderAddMoreLoop()}
                  {step === 'GOALS' && renderGoals()}
                  {step === 'SECURE_TRIAL' && ENABLE_STRIPE_ONBOARDING && renderSecureTrial()}
                  {step === 'COMPLETE' && renderComplete()}
                </>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Persistent Logo beneath the card */}
        <div className="flex justify-center mt-5">
          <img
            src="https://res.cloudinary.com/dlmbskxuc/image/upload/c_crop,w_175,h_170/v1745797729/logo1h_pmrhki.png"
            alt="Firsthold"
            style={{ width: 34, height: 34, opacity: 0.28, objectFit: 'contain' }}
            draggable={false}
          />
        </div>

        {/* Footer */}
        <div className="text-center pb-8 pt-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/20">
            © 2026 FirstHold &nbsp;·&nbsp;
            <a href="/privacy" className="hover:opacity-60 transition-opacity">Privacy Policy</a>
            &nbsp;·&nbsp;
            <a href="/terms" className="hover:opacity-60 transition-opacity">Terms of Service</a>
          </p>
        </div>
      </div>
      {planModalNode}
    </div>
  );
}
